import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Download, ExternalLink, FileText, X } from "lucide-react";
import {
  DONUSTURUCU_YETKISI,
  UYARI_KALIPLARI,
  belgeZorunluHatasi,
  dosyalayabilirMi,
  girdiKapsamlari,
  mrbSnBul,
  ndtGelenTuruMu,
  yetkiliMi,
  type BelgeKutugu,
  type GelenHedef,
  type GelenSatiri,
  type ProjeBelgeleriYaniti,
} from "@oasis/domain";
import { Band, Buton, Etiket, Hata } from "@/components/parcalar";
import { BOS_SAYFA, aralikSayisi, belgeLinki, sayfaGecerli, sayfalarGecerli, type SayfaSecimi } from "@/components/pdf-sayfa";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { ImzalayanAlani, ImzalayanUyarisi, useImzalayanSecimi } from "@/components/ndt-imzalayan";
import { BelgeOkumaBolumu } from "@/components/belge-okuma-bolumu";
import { NdtOkumaBolumu } from "@/components/ndt-okuma-bolumu";
import { WeldMapOkumaBolumu } from "@/components/weldmap-okuma-bolumu";
import { PwhtOkumaBolumu } from "@/components/pwht-okuma-bolumu";
import { NdtSertifikaOkumaBolumu } from "@/components/ndt-sertifika-okuma-bolumu";
import { WpqOkumaBolumu } from "@/components/wpq-okuma-bolumu";
import { KalibrasyonOkumaBolumu } from "@/components/kalibrasyon-okuma-bolumu";
import { SarfOkumaBolumu } from "@/components/sarf-okuma-bolumu";
import { WpsOkumaBolumu } from "@/components/wps-okuma-bolumu";
import { TeslimOkumaBolumu } from "@/components/teslim-okuma-bolumu";
import { MalzemeOkumaBolumu } from "@/components/malzeme-okuma-bolumu";
import { UygunlukOkumaBolumu } from "@/components/uygunluk-okuma-bolumu";
import { SatinAlmaOkumaBolumu } from "@/components/satin-alma-okuma-bolumu";
import { BaskaEkranKutusu, DevirFormu, FinishingFormu, GerekceAlani, IsFormu, KutukFormu, ProjeFormu, type SertlikDegerleri } from "@/components/belge-dosyala";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { imzalayanAlanlari, yuklemeAdimi } from "@/lib/ndt-imzalayan";
import { useOturum } from "@/lib/oturum";
import { pencereDenetimi, sayfalariOku } from "@/lib/proje-belgeleri";
import { tarihSaat, useVeri } from "@/lib/veri";
import {
  ETIKET_TONU,
  adDegerleri,
  adimImzasi,
  aktarimlaDosyala,
  ayniHedef,
  bicimHatasi,
  boyaRiserleri,
  canliBaglanti,
  devirle,
  durumAltMetni,
  eslesenExceller,
  etiketAdi,
  etkiler,
  formHedeften,
  gizleyenSinif,
  gorunmeCumlesi,
  hedefKur,
  ipucuMetni,
  kalipMetni,
  kapanisMetni,
  kapiMetni,
  kartAltMetni,
  kopyaMetni,
  ndtDisarida,
  ndtDosyalamaEngeli,
  ndtYerEngeli,
  okumaBolumuKapanmista,
  okumaDosyalamaEngeli,
  okumaYeriEngeli,
  rnYaz,
  satirUyariMetni,
  sertlikAdimiMi,
  sertlikHatasi,
  tekRiserPenceresi,
  turGruplari,
  turTanimi,
  uyariTonu,
  weldMapDosyalamaEngeli,
  weldMapYerEngeli,
  pwhtDosyalamaEngeli,
  pwhtOrtakMi,
  pwhtYerEngeli,
  ndtSertifikaDosyalamaEngeli,
  ndtSertifikaYerEngeli,
  wpqDosyalamaEngeli,
  wpqYerEngeli,
  kalibrasyonDosyalamaEngeli,
  kalibrasyonEtkisi,
  kalibrasyonYerEngeli,
  sarfDosyalamaEngeli,
  sarfYerEngeli,
  wpsDosyalamaEngeli,
  wpsYerEngeli,
  teslimDosyalamaEngeli,
  teslimYerEngeli,
  malzemeDosyalamaEngeli,
  malzemeFormSayfalari,
  malzemeSayfaEngeli,
  malzemeYerEngeli,
  uygunlukDosyalamaEngeli,
  uygunlukYerEngeli,
  satinAlmaDosyalamaEngeli,
  satinAlmaYerEngeli,
  yetkiCumlesi,
  type HedefFormu,
  type IsSecenegi,
  type KapiGorunumu,
  type KartYaniti,
  type KutukKaydi,
  type ListeYaniti,
  type SeciciRiseri,
  type YazimYaniti,
} from "@/lib/belgeler";

/**
 * D-074 Faz 1 (U-073): Documents'te bir dosyanın kartı (satırın altında, aynı anda tek kart). Üstte dosya, durum, uyarı çipleri ve
 * "Open file" / "Download file"; solda "What is this file?" ve "File by hand" formu (ya da dönüştürücü kutusu, "Open on" kutusu),
 * sağda uyarılar, "Effects of filing" / "What the gate does", son deneme; altta düğmeler ve satırın geçmişi. Dosyalama bugünkü
 * yoldandır (lib/belgeler.ts); kart yalnız çizer ve akışı çağırır. Kapanmış satırda kapanış cümlesi ve "Open where it is filed".
 * Hiçbir değer "okunmuş" gibi gösterilmez: öneri yalnız dosya adındandır ve öyle yazılır. D-074 Faz 2: hidrotest sertifikasının okunan
 * değerleri yalnız "Document reading" bölümünde gösterilir (`BelgeOkumaBolumu`); hiçbir form alanına dolmaz. D-074 Faz 2 · PR 9: NDT raporunun
 * okuması ayrı bölümdür (`NdtOkumaBolumu`); "Use this place" kartın tür+yer kaydını çağırır, "Use these pages" ve "Use this signer" yalnız formu
 * doldurur; dosyalama bugünkü "Confirm and file"dır.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";

/** Karttaki küçük bölüm başlığı. */
function Baslik({ children, ara }: { children: ReactNode; ara?: boolean }) {
  return <h3 className={cn("mb-1.5", ETIKET_SINIFI, ara && "mt-4")}>{children}</h3>;
}

/** Durum hapı (listeyle aynı). */
export function DurumHapi({ s, sar }: { s: Pick<GelenSatiri, "etiket">; sar?: boolean }) {
  // Listede dar sütunda uzun durum adı ("Waiting for someone who can file") satıra sarar, yan sütuna taşmaz.
  return <Etiket tone={ETIKET_TONU[s.etiket]} className={sar ? "max-w-full whitespace-normal" : undefined}>{etiketAdi(s.etiket)}</Etiket>;
}

/** Uyarı kodu çipi. */
export function UyariCipi({ kod, renk }: { kod: string; renk: string }) {
  return <Etiket tone={uyariTonu(renk)} className="font-semibold">{kod}</Etiket>;
}

const kucukBag = "inline-flex h-kontrol-kucuk items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 text-sm font-medium text-ink hover:border-line-strong";

/** D-074 Faz 2 (K37): PDF ve resim yeni görüntüleyicide açılır (görme kuralı aynı uçlarda); öteki biçimler bugünkü gibi dosyanın kendisi. */
const GORUNTULENIR = new Set(["pdf", "jpeg", "png"]);

/** "Open file" ve "Download file": gelen kutusundaki dosya (bugünkü `/api/dokuman/:id`; "Open file" PDF ve resimde görüntüleyici). */
function DosyaBaglari({ s }: { s: Pick<GelenSatiri, "dokumanId" | "ad" | "bicim"> }) {
  if (!s.dokumanId) return null;
  return (
    <>
      <a className={kucukBag} href={GORUNTULENIR.has(s.bicim) ? goruntuleyiciLinki(s.dokumanId) : belgeLinki(s.dokumanId)} target="_blank" rel="noreferrer"><ExternalLink size={13} />{cevir("Open file")}</a>
      <a className={kucukBag} href={belgeLinki(s.dokumanId)} download={s.ad ?? undefined}><Download size={13} />{cevir("Download file")}</a>
    </>
  );
}

/**
 * Uyarının çağrı kutusu (tasarım: prototip-ek.css `.bl-uyarilar li`): 4 px renkli sol çizgi, tonlu zemin, kod tonun renginde. Ekran denemesi
 * bulgusu (U-073): önceden düz satırdı ve kırmızıda bütün cümle kırmızıydı. Kenar rengi `!` ister: styles.css'teki katmansız
 * `* { border-color }` kuralı katmanlı yardımcı sınıfları ezer (sokum-parca.tsx'teki gibi).
 */
const UYARI_KUTUSU = "flex gap-2 rounded-md border-l-4 px-2 py-1.5 text-sm";
export const uyariKutusuTonu = (renk: string) => (renk === "kirmizi" ? "border-l-bad! bg-bad-bg" : renk === "sari" ? "border-l-warn! bg-warn-bg" : "border-l-line-strong! bg-surface-2");
export const uyariKoduRengi = (renk: string) => (renk === "kirmizi" ? "text-bad" : renk === "sari" ? "text-warn" : "text-ink-2");

/**
 * Uyarılar kutusu: satırın uyarıları ve aynı dosyanın yerleri. İnceleme bulgusu (U-073; §4.5, plan §5.2 adım 3 "hangi türde olursa
 * olsun"): yerler GEN-01 işareti olmasa da gösterilir (gri not): önceden reddedilmiş ya da referans yapılmış aynı dosya, bağı
 * reddedilmiş bir yükleme. "Remove from this list" yalnız bir kayda bağlı kopyada (GEN-01) çıkar.
 */
function UyariListesi({ s, kart }: { s: GelenSatiri; kart: KartYaniti | null }) {
  const satirlar = s.uyarilar.filter((u) => u.kod !== "GEN-01");
  const kopyalar = s.durum === "acik" ? (kart?.kopyalar ?? []) : [];
  if (!satirlar.length && !kopyalar.length) return null;
  return (
    <div>
      <Baslik>{cevir("Alerts")}</Baslik>
      <ul className="space-y-1.5">
        {satirlar.map((u) => (
          <li key={u.kod} className={cn(UYARI_KUTUSU, uyariKutusuTonu(u.renk))}>
            <span className={cn("w-[4.4rem] shrink-0 font-mono text-xs font-semibold leading-5", uyariKoduRengi(u.renk))}>{u.kod}</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{satirUyariMetni(u)}</span>
          </li>
        ))}
        {kopyalar.map((k, i) => (
          <li key={i} className={cn(UYARI_KUTUSU, uyariKutusuTonu("gri"), "flex-wrap gap-y-1")}>
            <span className={cn("w-[4.4rem] shrink-0 font-mono text-xs font-semibold leading-5", uyariKoduRengi("gri"))}>GEN-01</span>
            {/* Ekran denemesi bulgusu (U-073): telefonda bağ cümleyi dar bir sütuna sıkıştırıyordu; cümleye yer kalmazsa bağ alt satıra, sağa geçer. */}
            <span className="min-w-0 flex-[1_1_14rem] [overflow-wrap:anywhere]">{kopyaMetni(k)}</span>
            {k.yol && <Link to={k.yol as "/"} className="ml-auto text-sm font-medium text-orange underline">{cevir("Open where it is filed")}</Link>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Effects of filing" / "What the gate does" kutusu (tasarım `.bl-kutu`: bilgi zemini, başlık bilgi renginde; ekran denemesi bulgusu, önceden gri). */
function EtkiKutusu({ baslik, metinler }: { baslik: string; metinler: readonly string[] }) {
  if (!metinler.length) return null;
  return (
    <div className="rounded-lg border border-line bg-info-bg px-3 py-2 text-sm">
      <b className="text-info">{baslik}</b>
      <ul className="mt-1 list-disc space-y-1 pl-4 text-ink-2">{metinler.map((m) => <li key={m}>{m}</li>)}</ul>
    </div>
  );
}

/** Kapının yanıtı (toplu pencerenin satır cümlesi). */
function KapiSonucu({ k, rn }: { k: KapiGorunumu; rn: string | null }) {
  if (!k.etiketler.length && !k.aciklama) return null;
  return (
    <div className="rounded-md border border-line px-3 py-2 text-sm">
      <div className="flex flex-wrap gap-1">{k.etiketler.map((e) => <Etiket key={e.metin} tone={e.ton}>{e.metin}</Etiket>)}</div>
      {k.aciklama && <p className="mt-1 text-ink-2">{k.aciklama}</p>}
      {k.notlar.map((n) => <p key={n.metin} className={cn("mt-1 text-xs", n.ton === "warn" ? "text-warn" : n.ton === "ink-3" ? "text-ink-3" : "text-ink-2")}>{n.metin}</p>)}
      {k.akis && <Link to={(rn ? `/assembly/${rn}` : "/assembly") as "/"} className="mt-1 inline-block text-sm font-medium text-orange underline">{cevir("Open on {0}", cevir("Finishing"))}</Link>}
    </div>
  );
}

/**
 * Maskeli satırın kartı: kişisel ya da fiyatlı türde yükleyen yalnız "uploaded" bilgisini, durumu ve geri çekmeyi görür (K26). İnceleme
 * bulgusu (U-073): "Reject" öteki kapanışlar gibi iki adımdır (isteğe bağlı gerekçe, "Cancel"); yazım sürerken düğme pasif, hata görünür.
 * Kapanmış satır Faz 1'de yeniden açılmaz; önceden tek dokunuş satırı kapatıyordu.
 */
function MaskeliKart({ s, onYaz, onKapat, mesgul, hata }: { s: GelenSatiri; onYaz: (f: () => Promise<YazimYaniti>) => Promise<boolean>; onKapat: () => void; mesgul: boolean; hata: string | null }) {
  const [gerekce, setGerekce] = useState<string | null>(null);
  return (
    <article className="min-w-0 rounded-xl border border-line bg-surface">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line px-3.5 py-2.5">
        <FileText size={16} className="shrink-0 text-ink-3" />
        <div className="min-w-0 flex-1 text-md font-semibold">{s.turAdi ? cevir(s.turAdi) : "—"}<span className="block text-xs font-normal text-ink-3">{cevir("uploaded {0}", tarihSaat(s.ts))}</span></div>
        <DurumHapi s={s} />
        <button type="button" onClick={onKapat} className="rounded-md p-1 text-ink-3 hover:bg-surface-2" aria-label={cevir("Close")}><X size={16} /></button>
      </div>
      <div className="px-3.5 py-3 text-sm text-ink-2">{gorunmeCumlesi(s.sinif)}</div>
      {hata && <div className="px-3.5 pb-3"><Hata mesaj={hata} /></div>}
      {s.eylemler.reddet && (
        <div className="flex flex-wrap gap-2 rounded-b-xl border-t border-line bg-surface-2 px-3.5 py-2.5">
          {gerekce === null ? (
            <Buton disabled={mesgul} onClick={() => setGerekce("")}>{cevir("Reject")}</Buton>
          ) : (
            <div className="w-full space-y-2">
              <GerekceAlani id={`belge-${s.id}-gerekce`} deger={gerekce} onChange={setGerekce} zorunlu={false} disabled={mesgul} />
              <div className="flex flex-wrap gap-2">
                <Buton tur="tehlike" mesgul={mesgul} onClick={async () => { if (await onYaz(() => api.post<YazimYaniti>(`/belgeler/${s.id}/reddet`, { gerekce: gerekce.trim() }))) setGerekce(null); }}>{cevir("Reject")}</Buton>
                <Buton disabled={mesgul} onClick={() => setGerekce(null)}>{cevir("Cancel")}</Buton>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

const BOS_SERTLIK: SertlikDegerleri = { abm1: "", weld: "", abm2: "", birim: "HV", weldId: "" };

/**
 * Kart. `ozet` listedeki satırdır (kart yanıtı gelene kadar o çizilir); `onYenile` listeyi tazeler; `onMesaj` sayfanın üstündeki
 * geçici cümle (satır yazandan gizlendiğinde GEN-07 / GEN-08'in cümlesi).
 */
export function BelgeKarti({ ozet, onYenile, onKapat, onMesaj, onSatir }: { ozet: GelenSatiri; onYenile: () => Promise<void> | void; onKapat: () => void; onMesaj?: (m: string) => void; onSatir?: (s: GelenSatiri) => void }) {
  const { ben } = useOturum();
  const { veri: kart, yenile: kartYenile } = useVeri<KartYaniti>(`/belgeler/${ozet.id}`);
  const s = kart?.satir ?? ozet;
  // Sayfa kartı açık tutarken listedeki satırı da güncel gösterir (satır süzgeçten çıksa da kart yerinde kalır; inceleme bulgusu).
  useEffect(() => {
    if (kart?.satir) onSatir?.(kart.satir);
  }, [kart?.satir]);
  const [tur, setTur] = useState<string>(s.tur ?? "");
  const [form, setForm] = useState<HedefFormu>(() => formHedeften(s.hedef, s.ipucu, s.ad));
  const [sayfa, setSayfa] = useState<SayfaSecimi>(BOS_SAYFA);
  const [kutukAlan, setKutukAlan] = useState<Record<string, string>>({});
  const [projeMetin, setProjeMetin] = useState({ sayfalar: "", not: "" });
  const [sertlik, setSertlik] = useState<SertlikDegerleri>(BOS_SERTLIK);
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [kapi, setKapi] = useState<KapiGorunumu | null>(null);
  const [kapatma, setKapatma] = useState<{ islem: "referans" | "reddet"; gerekce: string } | null>(null);
  const [kararGerekce, setKararGerekce] = useState("");
  /** Satırı kişiden gizleyecek türün kaydı için onay kutusu açık mı. */
  const [turOnayi, setTurOnayi] = useState(false);

  // Tür sunucuda değişince (başka biri ya da bu kart) form o türün saklı yeriyle baştan kurulur.
  const sonTur = useRef(s.tur);
  useEffect(() => {
    if (sonTur.current === s.tur) return;
    sonTur.current = s.tur;
    setTur(s.tur ?? "");
    setForm(formHedeften(s.hedef, s.ipucu, s.ad));
  }, [s.tur, s.hedef, s.ipucu]);

  const t = turTanimi(tur);
  const hedefTuru = t?.hedef ?? null;
  const aile = t?.donusturucu?.aile ?? null;
  const acik = s.durum === "acik" && s.gorunum === "tam";
  const riserGerek = acik && (hedefTuru === "ht" || hedefTuru === "hat_ndt" || hedefTuru === "boya" || (hedefTuru === "proje" && girdiKapsamlari(t!.proje!).includes("riser")) || (hedefTuru === "donusturucu" && aile !== "mrb"));
  const isGerek = acik && (hedefTuru === "adim_raporu" || hedefTuru === "pwht" || hedefTuru === "weldmap" || hedefTuru === "urun_geldi");
  const kutukOkur = Boolean(ben && yetkiliMi(ben.rol, "belgeKutuk"));
  const riserV = useVeri<{ riserler: SeciciRiseri[] }>(riserGerek ? "/belgeler/secenekler/riser" : null);
  const isV = useVeri<{ isler: IsSecenegi[] }>(isGerek ? "/belgeler/secenekler/is" : null);
  const kutukV = useVeri<{ kayitlar: KutukKaydi[] }>(acik && hedefTuru === "kutuk" && kutukOkur ? `/belgeler/secenekler/kutuk?kutuk=${t!.kutuk}` : null);
  const projeV = useVeri<ProjeBelgeleriYaniti>(acik && hedefTuru === "proje" ? "/dosya-belge" : null);
  const ad = aile ? adDegerleri(aile, s.ad ?? "") : { rn: null, sn: null };
  const araAnahtari = aile ? (aile === "mrb" ? (form.sn.trim() || ad.sn) : (rnYaz(form.rn) || ad.rn)) : null;
  const eslesmeV = useVeri<ListeYaniti>(acik && aile && s.bicim === "pdf" && araAnahtari ? `/belgeler?suzgec=tumu&ara=${encodeURIComponent(araAnahtari)}` : null);

  const riserler = riserV.veri?.riserler ?? null;
  const isler = isV.veri?.isler ?? null;
  // Coating: yalnız seçicinin listelediği riserler (assembly sırasında, boya raporu yok); başka riser ucun reddine giderdi (inceleme bulgusu).
  const secilebilirRiserler = riserler ? (hedefTuru === "boya" ? boyaRiserleri(riserler) : riserler) : null;
  const hedef = hedefKur(tur || null, form, { isler: isler ?? undefined, riserler: secilebilirRiserler?.map((r) => r.rn), adRn: ad.rn, adSn: ad.sn });
  const is = isler?.find((x) => x.tamirId === form.tamirId) ?? null;
  const riser = riserler?.find((r) => r.rn === rnYaz(form.rn)) ?? null;
  const hatDurumu = riser?.hatlar.find((h) => h.hat === form.hat) ?? null;
  const exceller = aile && s.bicim === "pdf" ? eslesenExceller(eslesmeV.veri?.satirlar ?? [], s, aile, rnYaz(form.rn) || ad.rn, form.sn.trim() || ad.sn) : [];

  // Eşlenebilir tek excel varsa ilk açılışta işaretli gelir ("Send without excel" ile tek başına gönderilir).
  const ekOnerildi = useRef(false);
  useEffect(() => {
    if (ekOnerildi.current || !exceller.length || form.ekId) return;
    ekOnerildi.current = true;
    setForm((f) => ({ ...f, ekId: exceller[0]!.id }));
  }, [exceller.length, form.ekId]);

  const imzaBilgi = hedefTuru === "hat_ndt" ? { sorulur: true, yontemler: [] } : hedefTuru === "adim_raporu" ? adimImzasi(is) : { sorulur: false, yontemler: [] };
  const imza = useImzalayanSecimi(acik && imzaBilgi.sorulur, undefined, imzaBilgi.yontemler);

  /**
   * Seçili tür henüz kaydedilmedi mi (inceleme bulgusu, U-073: "What is this file?" artık her değişimde yazılmaz; ok tuşuyla gezinmek
   * türü kaydedip satırı gizleyebiliyordu, WCAG 3.2.2). Kaydedilmemiş türde dosyalama yetkisi ve gizleme tarayıcıda alan paketinin
   * kuralıyla hesaplanır; sunucu her yazımda yeniden denetler.
   */
  const turDegisti = s.durum === "acik" && s.gorunum === "tam" && s.eylemler.tur && tur !== (s.tur ?? "");
  const izleyen = ben ? { id: ben.id, rol: ben.rol } : null;
  const gizleyen = turDegisti && izleyen ? gizleyenSinif(izleyen, s, tur || null) : null;
  const yazabilir = turDegisti
    ? Boolean(izleyen && dosyalayabilirMi(izleyen, { yukleyenId: s.benim ? izleyen.id : "", ekYukleyenler: [], tur: tur || null }))
    : s.eylemler.dosyala || s.eylemler.donusturucu;

  /** Yazım: sonuç satırı yazandan gizlendiyse (J21) kart kapanır ve satırın sınıfının görünme cümlesi sayfada görünür. */
  const yaz = async (f: () => Promise<YazimYaniti | void>, anahtar = "yaz"): Promise<boolean> => {
    setMesgul(anahtar);
    setHata(null);
    try {
      const y = await f();
      if (y && "gizlendi" in y && y.gizlendi) {
        onMesaj?.(gorunmeCumlesi(turTanimi(y.satir?.tur ?? (tur || null))?.sinif ?? "belirsiz"));
        onKapat();
        await onYenile();
        return true;
      }
      await kartYenile();
      await onYenile();
      return true;
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
      return false;
    } finally {
      setMesgul(null);
    }
  };

  /** "What is this file?": seçim yalnız kartta değişir (form o türünkü olur); "Save type" ya da dosyalama düğmesi kaydeder. */
  const turSec = (yeni: string) => {
    setTur(yeni);
    setForm(formHedeften(null, s.ipucu, s.ad));
    setKapi(null);
    setTurOnayi(false);
    ekOnerildi.current = false;
  };
  /** Seçimi kayıtlı türe ve yerine döndürür. */
  const turGeriAl = () => {
    setTur(s.tur ?? "");
    setForm(formHedeften(s.hedef, s.ipucu, s.ad));
    setTurOnayi(false);
  };
  /** Türün kaydı (önerilen yer silinir); satırı gizleyen türde önce onay istenir. */
  const turYaz = () => api.post<YazimYaniti>(`/belgeler/${s.id}/tur`, { tur: tur || null });
  const turKaydet = async () => {
    if (gizleyen && !turOnayi) { setTurOnayi(true); return; }
    sonTur.current = tur || null;
    if (await yaz(turYaz, "tur")) setTurOnayi(false);
  };

  // Dosyalayamayan ama türü seçebilen kişi (ör. hidrotest sertifikasını yükleyen formen) yeri önerir: form tamamlanınca yer yazılır.
  // D-074 Faz 2 · PR 8 (T16; inceleme bulgusu): okunmuş hidrotest sertifikasında dosyalayan kişinin yer seçimi de yazılır; sunucu okumayı
  // o yerle yeniden karşılaştırır (dosyalama kapısı yeni okumanın uyarılarıyla). Sessiz yazım: alanlar kilitlenmez (yazan kişinin odağı
  // kaçmasın); hata kartın altında görünür.
  // D-074 Faz 2 · PR 9 (N19): okunmuş NDT raporunda da (yer değişince sunucu yeniden karşılaştırır; dosyalama kapısı yerdeki raporlara bakar).
  // D-074 Faz 2 · PR 10 (Z13): okunmuş weld map'te de (yer değişince sunucu yeniden karşılaştırır).
  const okunmusHt = (hedef?.tur === "ht" && Boolean(kart?.okuma?.okuma)) || (ndtGelenTuruMu(hedef?.tur) && Boolean(kart?.ndtOkuma?.okuma))
    || (hedef?.tur === "weldmap" && Boolean(kart?.weldMapOkuma?.okuma))
    // D-074 Faz 2 · PR 11 (G17): okunmuş PWHT grafiğinde de.
    || (hedef?.tur === "pwht" && Boolean(kart?.pwhtOkuma?.okuma))
    // D-074 Faz 3 · PR 14 (M6-15): okunmuş NDT sertifikasında da (inspektör seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "ndt_inspektor" && Boolean(kart?.ndtSertifikaOkuma?.okuma))
    // D-074 Faz 3 · PR 15 (M7-16): okunmuş kaynakçı belgesinde de (kaynakçı seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "kaynakci" && Boolean(kart?.wpqOkuma?.okuma))
    // D-074 Faz 3 · PR 16 (M1-15): okunmuş kalibrasyon sertifikasında da (alet seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "alet" && Boolean(kart?.kalibrasyonOkuma?.okuma))
    // D-074 Faz 3 · PR 17 (M8-16): okunmuş sarf belgesinde de (lot seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "sarf" && Boolean(kart?.sarfOkuma?.okuma))
    // D-074 Faz 3 · PR 18 (M9-17): okunmuş WPS belgesinde de (WPS seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "wps" && Boolean(kart?.wpsOkuma?.okuma))
    // D-074 Faz 4 · PR 20 (M2-13, M2-19): okunmuş packing list'te de (paket seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" && Boolean(kart?.teslimOkuma?.okuma))
    // D-074 Faz 4 · PR 21 (M3-12, M3-19): okunmuş malzeme sertifikasında da (paket seçimi kişinin yeri olur; sunucu yeniden karşılaştırır).
    || (hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" && Boolean(kart?.malzemeOkuma?.okuma))
    // D-074 Faz 4 · PR 22 (M4-11, M4-17): okunmuş CoC'de de.
    || (hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" && Boolean(kart?.uygunlukOkuma?.okuma))
    // D-074 Faz 4 · PR 23 (M5-11, M5-23): okunmuş PO'da da.
    || (hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" && Boolean(kart?.satinAlmaOkuma?.okuma));
  useEffect(() => {
    if (!acik || !s.eylemler.tur || (yazabilir && !okunmusHt) || !hedef || !tur || tur !== s.tur || ayniHedef(hedef, s.hedef)) return;
    const z = setTimeout(() => {
      api.post<YazimYaniti>(`/belgeler/${s.id}/tur`, { tur, hedef })
        .then(async (y) => {
          if (y.gizlendi) {
            onMesaj?.(gorunmeCumlesi(turTanimi(tur)?.sinif ?? "belirsiz"));
            onKapat();
          } else await kartYenile();
          await onYenile();
        })
        .catch((e: unknown) => setHata(e instanceof Error ? e.message : cevir("Error")));
    }, 800);
    return () => clearTimeout(z);
  }, [JSON.stringify(hedef), s.tur, JSON.stringify(s.hedef), yazabilir, acik, okunmusHt]);

  /**
   * D-074 Faz 2 · PR 9 (N18): "Use this place": bugünkü tür+yer kaydı (`POST :id/tur`, tür önerinin türü: formda kaydedilmemiş başka tür seçimi
   * gönderilmez); form o yerle kurulur, sunucu okumayı yeniden karşılaştırır.
   */
  const yerKaydet = async (h: GelenHedef) => {
    const t = h.tur;
    sonTur.current = t;
    setTur(t);
    const y = await api.post<YazimYaniti>(`/belgeler/${s.id}/tur`, { tur: t, hedef: h });
    if (y.gizlendi) {
      onMesaj?.(gorunmeCumlesi(turTanimi(t)?.sinif ?? "belirsiz"));
      onKapat();
      await onYenile();
      return;
    }
    setForm(formHedeften(h, s.ipucu, s.ad));
    await kartYenile();
    await onYenile();
  };

  if (s.gorunum === "yuklendi") return <MaskeliKart s={s} onYaz={(f) => yaz(f, "reddet")} onKapat={onKapat} mesgul={mesgul !== null} hata={hata} />;

  const baskasi = s.etiket === "dosyalaniyor" && s.dosyalayan && s.dosyalayan.ad !== ben?.ad;
  const benim = s.etiket === "dosyalaniyor" && s.dosyalayan && s.dosyalayan.ad === ben?.ad;
  const word = s.bicim === "word";
  const formHatasi = bicimHatasi(s.bicim, tur || null);
  // D-074 Faz 2: adım raporu kartı virgülle ayrılmış birden çok aralık alır (her aralık adımın ayrı raporu).
  const sayfaHatasi = s.bicim !== "pdf"
    ? null
    : hedefTuru === "adim_raporu" || hedefTuru === "pwht"
      ? (sayfalarGecerli(sayfa) ? null : cevir("One page (e.g. 9), a range (e.g. 1-4) or several ranges separated by commas (e.g. 1-2, 3-4). Must be within the PDF."))
      : (sayfaGecerli(sayfa) ? null : cevir("One page (e.g. 9) or a range (e.g. 1-4). Must be within the PDF."));
  // İnceleme bulguları (U-073): ucun reddedeceği eksikler düğmeden önce söylenir. Hardness ucun kuralıyla (Weld no., üç değer ya da
  // weld map'li işte hiçbiri); NDT sertifikasında yöntem ve seviye (`belgeZorunluHatasi`); MRB'de okunamayan SN.
  const sertlikEksik = hedefTuru === "adim_raporu" ? sertlikHatasi(is, { ...sertlik, weldId: is?.weldler.length ? sertlik.weldId : null }) : null;
  const kutukEksik = hedefTuru === "kutuk" && t?.kutuk ? belgeZorunluHatasi(t.kutuk as BelgeKutugu, { tur: t.belgeTuru ?? "", yontem: kutukAlan.yontem ?? null, seviye: kutukAlan.seviye ?? null }) : null;
  const snOkunmuyor = aile === "mrb" && Boolean(form.sn.trim()) && !mrbSnBul(form.sn.trim());
  /** "Confirm and file" neden pasif: Word → biçim → başkası dosyalıyor → yetki → yer eksik → sayfalar → zorunlu alanlar. Düğmenin altında yazar. */
  const engel = !acik
    ? null
    : word
      ? kalipMetni(UYARI_KALIPLARI["GEN-16"], ["Word", t ? t.ad : "documents"], [1])
      : formHatasi ?? (baskasi ? durumAltMetni(s) : null)
        ?? (!yazabilir ? yetkiCumlesi(tur) : null)
        // D-074 Faz 2 · PR 8 (T21, K1): okumanın kararsız kırmızısı ya da "Do not file" kararı; ret dosyalamada test kırmızıları kapatmaz.
        ?? (hedefTuru === "ht" ? (okumaYeriEngeli(kart?.okuma, hedef) ?? okumaDosyalamaEngeli(kart?.okuma, form.htSonuc === "ret")) : null)
        // D-074 Faz 2 · PR 9 (N22): okunmuş NDT raporu yalnız bir raporunun yerine; kapı yalnız o yerdeki raporların uyarılarına bakar.
        ?? (hedefTuru === "hat_ndt" || hedefTuru === "adim_raporu" ? (ndtYerEngeli(kart?.ndtOkuma, hedef) ?? ndtDosyalamaEngeli(kart?.ndtOkuma, hedef)) : null)
        // D-074 Faz 2 · PR 10 (Z14): okunmuş weld map yalnız okumanın yerine; kararsız kırmızı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "weldmap" ? (weldMapYerEngeli(kart?.weldMapOkuma, hedef) ?? weldMapDosyalamaEngeli(kart?.weldMapOkuma, hedef)) : null)
        // D-074 Faz 2 · PR 11 (G18): okunmuş PWHT grafiği yalnız okumanın yerine; kararsız kırmızı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "pwht" ? (pwhtYerEngeli(kart?.pwhtOkuma, hedef) ?? pwhtDosyalamaEngeli(kart?.pwhtOkuma, hedef)) : null)
        // D-074 Faz 3 · PR 14 (M6-15): okunmuş NDT sertifikası yalnız okumanın inspektörüne; kararsız NDT-09 ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (ndtSertifikaYerEngeli(kart?.ndtSertifikaOkuma, hedef) ?? ndtSertifikaDosyalamaEngeli(kart?.ndtSertifikaOkuma, hedef)) : null)
        // D-074 Faz 3 · PR 15 (M7-16): okunmuş kaynakçı belgesi yalnız okumanın kaynakçısına; kararsız kırmızı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (wpqYerEngeli(kart?.wpqOkuma, hedef) ?? wpqDosyalamaEngeli(kart?.wpqOkuma, hedef)) : null)
        // D-074 Faz 3 · PR 16 (M1-15): okunmuş kalibrasyon sertifikası yalnız bir cihazının aletine; o cihazın kararsız kırmızısı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (kalibrasyonYerEngeli(kart?.kalibrasyonOkuma, hedef) ?? kalibrasyonDosyalamaEngeli(kart?.kalibrasyonOkuma, hedef)) : null)
        // D-074 Faz 3 · PR 17 (M8-16): okunmuş sarf belgesi yalnız okumanın lotuna; kararsız kırmızı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (sarfYerEngeli(kart?.sarfOkuma, hedef) ?? sarfDosyalamaEngeli(kart?.sarfOkuma, hedef)) : null)
        // D-074 Faz 3 · PR 18 (M9-17): okunmuş WPS belgesi yalnız okumanın WPS'ine; kararsız kırmızı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (wpsYerEngeli(kart?.wpsOkuma, hedef) ?? wpsDosyalamaEngeli(kart?.wpsOkuma, hedef)) : null)
        // D-074 Faz 4 · PR 20 (M2-19): okunmuş packing list yalnız bir kaleminin paketine; o kalemin kararsız kırmızısı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (teslimYerEngeli(kart?.teslimOkuma, hedef) ?? teslimDosyalamaEngeli(kart?.teslimOkuma, hedef)) : null)
        // D-074 Faz 4 · PR 21 (M3-19): okunmuş malzeme sertifikası yalnız bir sertifikasının paketine, o sertifikanın sayfalarıyla; o sertifikanın kararsız kırmızısı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk"
          ? (malzemeYerEngeli(kart?.malzemeOkuma, hedef) ?? malzemeSayfaEngeli(kart?.malzemeOkuma, hedef, malzemeFormSayfalari(sayfa)) ?? malzemeDosyalamaEngeli(kart?.malzemeOkuma, hedef))
          : null)
        // D-074 Faz 4 · PR 22 (M4-17): okunmuş CoC yalnız bir kaleminin paketine; o kalemin kararsız kırmızısı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (uygunlukYerEngeli(kart?.uygunlukOkuma, hedef) ?? uygunlukDosyalamaEngeli(kart?.uygunlukOkuma, hedef)) : null)
        // D-074 Faz 4 · PR 23 (M5-23): okunmuş PO yalnız bir satırının paketine; o satırın kararsız kırmızısı ya da "Do not file" varken dosyalanmaz.
        ?? (hedefTuru === "kutuk" ? (satinAlmaYerEngeli(kart?.satinAlmaOkuma, hedef) ?? satinAlmaDosyalamaEngeli(kart?.satinAlmaOkuma, hedef)) : null)
        ?? (snOkunmuyor ? cevir("Write the SN like 1306722-1.") : null)
        // D-074 K7: dosya adı "reject" diyorsa sertifika sonucu seçilmeden dosyalanmaz.
        ?? (hedefTuru === "ht" && form.htSonuc === "" ? cevir("Choose the certificate result.") : null)
        ?? (!hedef ? cevir("Choose where the file belongs.") : null)
        ?? sayfaHatasi
        ?? (sertlikEksik ? cevir(sertlikEksik) : null)
        // Ekran denemesi bulgusu (U-073): Registers'ın "Documents: {0}" kalıbı bu sayfada "Documents: select the method" okunuyordu.
        ?? (kutukEksik ? cevir("NDT certificate: {0}.", cevir(kutukEksik)) : null);

  const etkiBaglami = {
    htVar: (hatDurumu?.ht ?? 0) > 0,
    htRet: hedefTuru === "ht" && form.htSonuc === "ret",
    kuyrukta: Boolean(riser?.kuyrukta),
    kayitYok: Boolean(hatDurumu?.kayitYok),
    adimRaporuVar: Boolean(is?.adimRaporuVar),
    aralikSayisi: (hedefTuru === "adim_raporu" || hedefTuru === "pwht") && s.bicim === "pdf" ? aralikSayisi(sayfa) : 0,
    sertlik: sertlikAdimiMi(is?.aktifAdim),
    weldMapVar: Boolean(is?.weldMapVar),
    pn: is?.bom.find((b) => b.id === form.bomId)?.pn ?? null,
    // D-074 Faz 2 · PR 9 (N30): formun yerine düşmeyen raporlar (yalnız formun yerinde okunmuş rapor varken).
    ndtDisarida: kart?.ndtOkuma?.okuma && hedef && !ndtYerEngeli(kart.ndtOkuma, hedef) ? ndtDisarida(kart.ndtOkuma, hedef) : undefined,
    // D-074 Faz 2 · PR 10 (Z14): okunmuş weld map'te kaynak tarihleri dosyalamadan sonra kaydedilir.
    weldMapOkundu: Boolean(kart?.weldMapOkuma?.okuma),
    // D-074 Faz 2 · PR 11 (G18): okunmuş PWHT grafiğinin bir grafiği ortak kanıt.
    pwhtOrtak: pwhtOrtakMi(kart?.pwhtOkuma),
    // D-074 Faz 3 · PR 14: NDT sertifikasının etkilerinde inspektörün adı (formda seçilen kayıt).
    // D-074 Faz 3 · PR 15: kaynakçı belgesinin etkilerinde kaynakçının kodu (formda seçilen kayıt; ad yazılmaz).
    kayitAdi: hedef?.tur === "kutuk" && hedef.kutuk === "ndt_inspektor" ? kutukV.veri?.kayitlar.find((x) => x.anahtar === hedef.anahtar)?.etiket
      : hedef?.tur === "kutuk" && hedef.kutuk === "kaynakci" ? hedef.anahtar : undefined,
    wpqOkundu: Boolean(kart?.wpqOkuma?.okuma),
    // D-074 Faz 3 · PR 16 (M1-17 … M1-19): okunmuş kalibrasyon sertifikasında Calibration end'in bugünkü ve sonraki tarihi (formun değerleriyle), cihaz sayısı.
    kalibrasyonOkundu: Boolean(kart?.kalibrasyonOkuma?.okuma),
    kalibrasyonCokCihaz: (kart?.kalibrasyonOkuma?.cihazlar.length ?? 0) > 1,
    kalibrasyonEtkisi: kalibrasyonEtkisi(kart?.kalibrasyonOkuma, hedef, kutukAlan),
    // D-074 Faz 3 · PR 17 (M8-18, M8-19): okunmuş sarf belgesinde lotun değeri dosyalamadan sonra kaydedilir; formun lotu.
    sarfOkundu: Boolean(kart?.sarfOkuma?.okuma),
    sarfLot: hedef?.tur === "kutuk" && hedef.kutuk === "sarf" ? hedef.anahtar : null,
    // D-074 Faz 3 · PR 18 (M9-19, M9-22): okunmuş WPS belgesinde eski sürüm dosyalamadan sonra işaretlenir; formun WPS'i.
    wpsOkundu: Boolean(kart?.wpsOkuma?.okuma),
    wpsKod: hedef?.tur === "kutuk" && hedef.kutuk === "wps" ? hedef.anahtar : null,
    // D-074 Faz 4 · PR 20 (M2-17, M2-18, M2-21): okunmuş packing list öteki paketlere dosyalamadan sonra eklenir; formun paketinin PKG no'su.
    teslimOkundu: Boolean(kart?.teslimOkuma?.okuma),
    teslimPkg: hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? kutukV.veri?.kayitlar.find((x) => x.anahtar === hedef.anahtar)?.etiket.split(" · ")[0] ?? null : null,
    // D-074 Faz 4 · PR 21 (M3-20, M3-22, M3-24): okunmuş malzeme sertifikasında öteki sertifikalar dosyalamadan sonra eklenir; formun paketinin PKG no'su ve PN'i.
    malzemeOkundu: Boolean(kart?.malzemeOkuma?.okuma),
    malzemePkg: hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? kutukV.veri?.kayitlar.find((x) => x.anahtar === hedef.anahtar)?.etiket.split(" · ")[0] ?? null : null,
    malzemePn: hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? kutukV.veri?.kayitlar.find((x) => x.anahtar === hedef.anahtar)?.etiket.split(" · ")[1] ?? null : null,
    // D-074 Faz 4 · PR 22 (M4-18, M4-20): okunmuş CoC öteki paketlere dosyalamadan sonra eklenir; formun paketinin PKG no'su.
    uygunlukOkundu: Boolean(kart?.uygunlukOkuma?.okuma),
    uygunlukPkg: hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? kutukV.veri?.kayitlar.find((x) => x.anahtar === hedef.anahtar)?.etiket.split(" · ")[0] ?? null : null,
    // D-074 Faz 4 · PR 23 (M5-24, M5-25): okunmuş PO öteki paketlere dosyalamadan sonra eklenir; formun paketinin PKG no'su.
    satinAlmaOkundu: Boolean(kart?.satinAlmaOkuma?.okuma),
    satinAlmaPkg: hedef?.tur === "kutuk" && hedef.kutuk === "stok_paket" ? kutukV.veri?.kayitlar.find((x) => x.anahtar === hedef.anahtar)?.etiket.split(" · ")[0] ?? null : null,
  };
  // MRB: yazılan SN okunamıyorsa kapının yaptıkları gösterilmez (eksik; düğmenin altındaki cümle söyler).
  const mrbSn = form.sn.trim() ? mrbSnBul(form.sn.trim()) : ad.sn;
  const yerMetni = aile === "mrb" ? (mrbSn ? `SN ${mrbSn}` : null) : (rnYaz(form.rn) || ad.rn);
  const pencere = aile ? tekRiserPenceresi(aile, rnYaz(form.rn) || ad.rn) : null;
  const riserYok = Boolean(aile && aile !== "mrb" && !yerMetni);

  /** "Confirm and file": Registers servis yolu; öteki hedefler bugünkü uca aktarma. NDT'de imzalayan adımı önce. */
  /** Kaydedilmemiş tür dosyalamadan önce kaydedilir (dosyalayan kişide tür satırı gizlemez); gizlenirse akış durur. */
  const onceTur = async (): Promise<YazimYaniti | null> => {
    if (!turDegisti) return null;
    sonTur.current = tur || null;
    const y = await turYaz();
    return y.gizlendi ? y : null;
  };

  const dosyala = async (onay = false) => {
    if (!hedef || engel) return;
    if (hedef.tur === "kutuk") {
      const a = sayfa.kismi ? sayfalariOku(sayfa.sayfalar) : { sayfaBas: null, sayfaSon: null };
      const alanlar = Object.fromEntries(Object.entries(kutukAlan).map(([k, v]) => [k, v.trim() || null]));
      await yaz(async () => (await onceTur()) ?? api.post<YazimYaniti>(`/belgeler/${s.id}/kutuk`, { kutuk: hedef.kutuk, anahtar: hedef.anahtar, tur: hedef.belgeTuru, ...a, ...alanlar }), "dosyala");
      return;
    }
    if (imzaBilgi.sorulur && !onay && yuklemeAdimi(true, imza.secili, imza.bulgular) !== "gonder") {
      imza.uyariAc();
      return;
    }
    await yaz(async () => {
      const gizli = await onceTur();
      if (gizli) return gizli;
      const r = await aktarimlaDosyala(canliBaglanti, s, hedef as GelenHedef, {
        sayfa,
        imzalayan: imzaBilgi.sorulur ? imzalayanAlanlari(imza.secili, onay) : undefined,
        sertlik: hedefTuru === "adim_raporu" && sertlikAdimiMi(is?.aktifAdim) ? { ...sertlik, weldId: is?.weldler.length ? sertlik.weldId : null } : null,
        proje: (dokumanId) => {
          const p = hedef.tur === "proje" ? hedef : null;
          const v = projeV.veri?.proje;
          if (!p) return { govde: null, hata: "Choose where the file belongs." };
          const d = pencereDenetimi(
            { tur: p.belgeTuru, secenek: { kapsam: p.kapsam, anahtar: p.anahtar }, dosya: { id: dokumanId, ad: s.ad ?? "", toplam: s.sayfaSayisi ?? 0 }, sayfalar: projeMetin.sayfalar, not: projeMetin.not },
            { isEmirleri: v?.isEmirleri.map((e) => e.no) ?? [], etkin: p.kapsam === "riser" ? [] : (v?.belgeler ?? []) },
          );
          return d;
        },
      });
      imza.uyariKapat();
      if (r.tur === "reddedildi") throw new Error(cevir(r.mesaj));
      return r.yanit;
    }, "dosyala");
  };

  /** "Send to converter" (excel'le ya da "Send without excel"). */
  const gonder = async (excelli: boolean) => {
    if (!hedef || hedef.tur !== "donusturucu") return;
    const h = { ...hedef, ekId: excelli ? form.ekId : null };
    await yaz(async () => {
      const gizli = await onceTur();
      if (gizli) return gizli;
      const r = await devirle(canliBaglanti, s, h, ad);
      if (r.tur === "zaten") return r.yanit;
      setKapi(r.kapi);
      if (!r.isaret && r.mesaj && !r.kapi.aciklama) throw new Error(cevir(r.mesaj));
      return r.isaret ?? undefined;
    }, excelli ? "gonder" : "excelsiz");
  };

  /** "File as reference" / "Reject": kutu yalnız yazım başarılıysa kapanır (hata kutunun altında görünür, gerekçe kaybolmaz). */
  const kapat = async (islem: "referans" | "reddet", gerekce: string) => {
    if (await yaz(() => api.post<YazimYaniti>(`/belgeler/${s.id}/${islem}`, { gerekce }), islem)) setKapatma(null);
  };

  const kararKutusu = acik && word && Boolean(ben && yetkiliMi(ben.rol, "belgeUyari")) && (s.eylemler.referans || s.eylemler.reddet);
  const ipucu = kart?.ipucu ?? s.ipucu;
  const kapanis = kapanisMetni(s, s.durum === "zaten_var" && kart?.kopyalar[0] ? kopyaMetni(kart.kopyalar[0]) : null);
  const gen01 = acik && s.uyarilar.some((u) => u.kod === "GEN-01");
  // Ekran denemesi bulgusu (U-073): renkli kenar `!` ister (styles.css'teki katmansız `* { border-color }`); önceden Word kartı gri kenarlıydı.
  const kenar = s.etiket === "karar_bekliyor" ? "border-bad!" : imza.uyari ? "border-warn!" : "border-line";

  return (
    <article className={cn("@container min-w-0 rounded-xl border bg-surface", kenar)} data-kart={s.id}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-line px-3.5 py-2.5">
        <FileText size={16} className="shrink-0 text-ink-3" />
        <div className="min-w-0 flex-[1_1_260px] text-md font-semibold [overflow-wrap:anywhere]">
          {s.ad}
          <span className="block text-xs font-normal text-ink-3">{kartAltMetni(s)}</span>
        </div>
        <DurumHapi s={s} />
        {s.uyarilar.map((u) => <UyariCipi key={u.kod} kod={u.kod} renk={u.renk} />)}
        <DosyaBaglari s={s} />
        <button type="button" onClick={onKapat} className="rounded-md p-1 text-ink-3 hover:bg-surface-2" aria-label={cevir("Close")}><X size={16} /></button>
      </div>

      {!acik ? (
        <div className="space-y-2 px-3.5 py-3 text-sm">
          {kapanis && <p className="font-medium">{kapanis}{s.kapanis?.kim ? <span className="font-normal text-ink-3"> · {s.kapanis.kim}, {tarihSaat(s.kapanis.ts)}</span> : null}</p>}
          {kapi && <KapiSonucu k={kapi} rn={rnYaz(form.rn) || ad.rn} />}
          {s.hedefYolu && (s.durum === "dosyalandi" || s.durum === "donusturucu") && <Link to={s.hedefYolu as "/"} className="inline-block font-medium text-orange underline">{cevir("Open where it is filed")}</Link>}
          {s.durum === "zaten_var" && kart?.kopyalar[0]?.yol && <Link to={kart.kopyalar[0].yol as "/"} className="inline-block font-medium text-orange underline">{cevir("Open where it is filed")}</Link>}
          {kart?.okuma && okumaBolumuKapanmista(kart.okuma) && <div className="pt-1"><BelgeOkumaBolumu satirId={s.id} k={kart.okuma} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {kart?.ndtOkuma && okumaBolumuKapanmista(kart.ndtOkuma) && <div className="pt-1"><NdtOkumaBolumu satirId={s.id} k={kart.ndtOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {kart?.weldMapOkuma && okumaBolumuKapanmista(kart.weldMapOkuma) && <div className="pt-1"><WeldMapOkumaBolumu satirId={s.id} k={kart.weldMapOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {kart?.pwhtOkuma && okumaBolumuKapanmista(kart.pwhtOkuma) && <div className="pt-1"><PwhtOkumaBolumu satirId={s.id} k={kart.pwhtOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 3 · PR 14 (M6-17): dosyalanmış sertifikanın öteki yöntem satırları burada "Add to Registers" ile eklenir. */}
          {kart?.ndtSertifikaOkuma && okumaBolumuKapanmista(kart.ndtSertifikaOkuma) && <div className="pt-1"><NdtSertifikaOkumaBolumu satirId={s.id} k={kart.ndtSertifikaOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 3 · PR 15 (M7-18): dosyalanmış kaynakçı belgesinin imzalı süreklilik satırları burada "Add to Registers" ile eklenir. */}
          {kart?.wpqOkuma && okumaBolumuKapanmista(kart.wpqOkuma) && <div className="pt-1"><WpqOkumaBolumu satirId={s.id} k={kart.wpqOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 3 · PR 16 (M1-17): dosyalanmış kalibrasyon sertifikasının öteki cihazları burada "Add to Registers" ile eklenir. */}
          {kart?.kalibrasyonOkuma && okumaBolumuKapanmista(kart.kalibrasyonOkuma) && <div className="pt-1"><KalibrasyonOkumaBolumu satirId={s.id} k={kart.kalibrasyonOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 3 · PR 17 (M8-18): dosyalanmış sarf belgesinin değeri burada "Record on the lot" ile lota yazılır (yalnız lotun alanı boşken). */}
          {kart?.sarfOkuma && okumaBolumuKapanmista(kart.sarfOkuma) && <div className="pt-1"><SarfOkumaBolumu satirId={s.id} k={kart.sarfOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 3 · PR 18 (M9-19): dosyalanmış WPS ya da PQR'ın aynı WPS'teki eski belgeleri burada tek tek "Mark as superseded" ile işaretlenir (PM ya da sistem yöneticisi). */}
          {kart?.wpsOkuma && okumaBolumuKapanmista(kart.wpsOkuma) && <div className="pt-1"><WpsOkumaBolumu satirId={s.id} k={kart.wpsOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 4 · PR 20 (M2-17, M2-18): dosyalanmış packing list'te liste öteki paketlere "Add the list" ile eklenir; paketi olmayan kalemde PM ya da sistem yöneticisi "Open stock package" der. */}
          {kart?.teslimOkuma && okumaBolumuKapanmista(kart.teslimOkuma) && <div className="pt-1"><TeslimOkumaBolumu satirId={s.id} k={kart.teslimOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 4 · PR 21 (M3-20, M3-22): dosyalanmış malzeme sertifikasında öteki sertifikalar "Add to package" ile eklenir; PM ya da sistem yöneticisi paketin boş heat / PO alanını "Record on the package" ile doldurur. */}
          {kart?.malzemeOkuma && okumaBolumuKapanmista(kart.malzemeOkuma) && <div className="pt-1"><MalzemeOkumaBolumu satirId={s.id} k={kart.malzemeOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 4 · PR 22 (M4-18): dosyalanmış CoC öteki kalemlerin paketlerine "Add to package" ile eklenir. */}
          {kart?.uygunlukOkuma && okumaBolumuKapanmista(kart.uygunlukOkuma) && <div className="pt-1"><UygunlukOkumaBolumu satirId={s.id} k={kart.uygunlukOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
          {/* D-074 Faz 4 · PR 23 (M5-24): dosyalanmış PO öteki satırların paketlerine "Add to package" ile eklenir (yalnız PM ve sistem yöneticisi). */}
          {kart?.satinAlmaOkuma && okumaBolumuKapanmista(kart.satinAlmaOkuma) && <div className="pt-1"><SatinAlmaOkumaBolumu satirId={s.id} k={kart.satinAlmaOkuma} hedef={s.hedef} onYenile={() => { void kartYenile(); void onYenile(); }} salt /></div>}
        </div>
      ) : (
        // Ekran denemesi bulgusu (U-073): iki sütun ekranın değil kartın genişliğiyle (tasarım: 52rem altında tek sütun); boş sağ sütun çizilmez.
        <div className="grid @min-[52rem]:grid-cols-2">
          <div className="min-w-0 px-3.5 py-3">
            <Baslik>{cevir("What is this file?")}</Baslik>
            <select className="h-kontrol w-full rounded-md border border-line-strong bg-bg px-2.5 text-sm text-ink outline-none focus:border-orange disabled:opacity-60" aria-label={cevir("What is this file?")} value={tur} disabled={!s.eylemler.tur} onChange={(e) => turSec(e.target.value)}>
              <option value="">{cevir("Choose the type...")}</option>
              {turGruplari().map((g) => (
                <optgroup key={g.grup} label={g.ad}>
                  {g.turler.map((x) => <option key={x.anahtar} value={x.anahtar}>{x.ad}</option>)}
                </optgroup>
              ))}
            </select>
            {turDegisti && (
              turOnayi && gizleyen ? (
                <Band tip="not" className="mt-2">
                  {gorunmeCumlesi(gizleyen)} {cevir("You will no longer see this file after saving the type.")}
                  <span className="mt-2 flex flex-wrap gap-2">
                    <Buton kucuk tur="birincil" mesgul={mesgul === "tur"} onClick={turKaydet}>{cevir("Save type")}</Buton>
                    <Buton kucuk disabled={mesgul !== null} onClick={turGeriAl}>{cevir("Cancel")}</Buton>
                  </span>
                </Band>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  <Buton kucuk tur={yazabilir ? "normal" : "birincil"} mesgul={mesgul === "tur"} disabled={mesgul !== null && mesgul !== "tur"} onClick={turKaydet}>{cevir("Save type")}</Buton>
                  <Buton kucuk disabled={mesgul !== null} onClick={turGeriAl}>{cevir("Cancel")}</Buton>
                </div>
              )
            )}
            {ipucu && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-2">
                <span className="min-w-0">{cevir("From the file name (a hint only): {0}", ipucuMetni(ipucu))}</span>
                <Buton kucuk disabled={!s.eylemler.tur || tur === ipucu.tur} onClick={() => turSec(ipucu.tur)}>{cevir("Use")}</Buton>
              </div>
            )}
            {formHatasi && <p className="mt-2 text-sm text-bad">{formHatasi}</p>}
            {t?.hedef && !word && !formHatasi && t.hedef !== "donusturucu" && (
              <>
                <Baslik ara>{cevir("File by hand")}</Baslik>
                {(t.hedef === "ht" || t.hedef === "hat_ndt" || t.hedef === "boya") && <FinishingFormu tanim={t} satir={s} riserler={riserler} durum={{ form, setForm, sayfa, setSayfa, disabled: mesgul !== null || imza.uyari }} />}
                {/* D-074 Faz 2 · PR 8 (prototip kart 4): okumanın kırmızısı karar beklerken dosyalama kapalı; bant formun altında. */}
                {t.hedef === "ht" && (okumaYeriEngeli(kart?.okuma, hedef)
                  ? <Band tip="not" className="mt-2">{okumaYeriEngeli(kart?.okuma, hedef)}</Band>
                  : okumaDosyalamaEngeli(kart?.okuma, form.htSonuc === "ret") && <Band tip="duvar" className="mt-2">{okumaDosyalamaEngeli(kart?.okuma, form.htSonuc === "ret")}</Band>)}
                {/* D-074 Faz 2 · PR 9 (N22): okunmuş NDT raporunun yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "hat_ndt" && (ndtYerEngeli(kart?.ndtOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{ndtYerEngeli(kart?.ndtOkuma, hedef)}</Band>
                  : ndtDosyalamaEngeli(kart?.ndtOkuma, hedef) && <Band tip="duvar" className="mt-2">{ndtDosyalamaEngeli(kart?.ndtOkuma, hedef)}</Band>)}
                {(t.hedef === "adim_raporu" || t.hedef === "pwht" || t.hedef === "weldmap" || t.hedef === "urun_geldi") && <IsFormu tanim={t} satir={s} isler={isler} sertlik={sertlik} setSertlik={setSertlik} durum={{ form, setForm, sayfa, setSayfa, disabled: mesgul !== null || imza.uyari }} />}
                {t.hedef === "weldmap" && (weldMapYerEngeli(kart?.weldMapOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{weldMapYerEngeli(kart?.weldMapOkuma, hedef)}</Band>
                  : weldMapDosyalamaEngeli(kart?.weldMapOkuma, hedef) && <Band tip="duvar" className="mt-2">{weldMapDosyalamaEngeli(kart?.weldMapOkuma, hedef)}</Band>)}
                {/* D-074 Faz 2 · PR 11 (G18): okunmuş PWHT grafiğinin yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "pwht" && (pwhtYerEngeli(kart?.pwhtOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{pwhtYerEngeli(kart?.pwhtOkuma, hedef)}</Band>
                  : pwhtDosyalamaEngeli(kart?.pwhtOkuma, hedef) && <Band tip="duvar" className="mt-2">{pwhtDosyalamaEngeli(kart?.pwhtOkuma, hedef)}</Band>)}
                {t.hedef === "adim_raporu" && (ndtYerEngeli(kart?.ndtOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{ndtYerEngeli(kart?.ndtOkuma, hedef)}</Band>
                  : ndtDosyalamaEngeli(kart?.ndtOkuma, hedef) && <Band tip="duvar" className="mt-2">{ndtDosyalamaEngeli(kart?.ndtOkuma, hedef)}</Band>)}
                {t.hedef === "kutuk" && <KutukFormu tanim={t} satir={s} kayitlar={kutukV.veri?.kayitlar ?? (kutukOkur ? null : [])} kayitHatasi={kutukV.hata} alanlar={kutukAlan} setAlanlar={setKutukAlan} durum={{ form, setForm, sayfa, setSayfa, disabled: mesgul !== null }} />}
                {/* D-074 Faz 3 · PR 14 (M6-15): okunmuş NDT sertifikasının yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (ndtSertifikaYerEngeli(kart?.ndtSertifikaOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{ndtSertifikaYerEngeli(kart?.ndtSertifikaOkuma, hedef)}</Band>
                  : ndtSertifikaDosyalamaEngeli(kart?.ndtSertifikaOkuma, hedef) && <Band tip="duvar" className="mt-2">{ndtSertifikaDosyalamaEngeli(kart?.ndtSertifikaOkuma, hedef)}</Band>)}
                {/* D-074 Faz 3 · PR 15 (M7-16): okunmuş kaynakçı belgesinin yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (wpqYerEngeli(kart?.wpqOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{wpqYerEngeli(kart?.wpqOkuma, hedef)}</Band>
                  : wpqDosyalamaEngeli(kart?.wpqOkuma, hedef) && <Band tip="duvar" className="mt-2">{wpqDosyalamaEngeli(kart?.wpqOkuma, hedef)}</Band>)}
                {/* D-074 Faz 3 · PR 16 (M1-15): okunmuş kalibrasyon sertifikasının yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (kalibrasyonYerEngeli(kart?.kalibrasyonOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{kalibrasyonYerEngeli(kart?.kalibrasyonOkuma, hedef)}</Band>
                  : kalibrasyonDosyalamaEngeli(kart?.kalibrasyonOkuma, hedef) && <Band tip="duvar" className="mt-2">{kalibrasyonDosyalamaEngeli(kart?.kalibrasyonOkuma, hedef)}</Band>)}
                {/* D-074 Faz 3 · PR 17 (M8-16): okunmuş sarf belgesinin yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (sarfYerEngeli(kart?.sarfOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{sarfYerEngeli(kart?.sarfOkuma, hedef)}</Band>
                  : sarfDosyalamaEngeli(kart?.sarfOkuma, hedef) && <Band tip="duvar" className="mt-2">{sarfDosyalamaEngeli(kart?.sarfOkuma, hedef)}</Band>)}
                {/* D-074 Faz 3 · PR 18 (M9-17): okunmuş WPS belgesinin yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (wpsYerEngeli(kart?.wpsOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{wpsYerEngeli(kart?.wpsOkuma, hedef)}</Band>
                  : wpsDosyalamaEngeli(kart?.wpsOkuma, hedef) && <Band tip="duvar" className="mt-2">{wpsDosyalamaEngeli(kart?.wpsOkuma, hedef)}</Band>)}
                {/* D-074 Faz 4 · PR 20 (M2-19): okunmuş packing list'in yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (teslimYerEngeli(kart?.teslimOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{teslimYerEngeli(kart?.teslimOkuma, hedef)}</Band>
                  : teslimDosyalamaEngeli(kart?.teslimOkuma, hedef) && <Band tip="duvar" className="mt-2">{teslimDosyalamaEngeli(kart?.teslimOkuma, hedef)}</Band>)}
                {/* D-074 Faz 4 · PR 21 (M3-19): okunmuş malzeme sertifikasının yer, sayfa ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (malzemeYerEngeli(kart?.malzemeOkuma, hedef) ?? malzemeSayfaEngeli(kart?.malzemeOkuma, hedef, malzemeFormSayfalari(sayfa))
                  ? <Band tip="not" className="mt-2">{malzemeYerEngeli(kart?.malzemeOkuma, hedef) ?? malzemeSayfaEngeli(kart?.malzemeOkuma, hedef, malzemeFormSayfalari(sayfa))}</Band>
                  : malzemeDosyalamaEngeli(kart?.malzemeOkuma, hedef) && <Band tip="duvar" className="mt-2">{malzemeDosyalamaEngeli(kart?.malzemeOkuma, hedef)}</Band>)}
                {/* D-074 Faz 4 · PR 22 (M4-17): okunmuş CoC'nin yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (uygunlukYerEngeli(kart?.uygunlukOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{uygunlukYerEngeli(kart?.uygunlukOkuma, hedef)}</Band>
                  : uygunlukDosyalamaEngeli(kart?.uygunlukOkuma, hedef) && <Band tip="duvar" className="mt-2">{uygunlukDosyalamaEngeli(kart?.uygunlukOkuma, hedef)}</Band>)}
                {/* D-074 Faz 4 · PR 23 (M5-23): okunmuş PO'nun yer ve karar kapısı; bant formun altında. */}
                {t.hedef === "kutuk" && (satinAlmaYerEngeli(kart?.satinAlmaOkuma, hedef)
                  ? <Band tip="not" className="mt-2">{satinAlmaYerEngeli(kart?.satinAlmaOkuma, hedef)}</Band>
                  : satinAlmaDosyalamaEngeli(kart?.satinAlmaOkuma, hedef) && <Band tip="duvar" className="mt-2">{satinAlmaDosyalamaEngeli(kart?.satinAlmaOkuma, hedef)}</Band>)}
                {t.hedef === "proje" && <ProjeFormu tanim={t} satir={s} proje={projeV.veri?.proje ?? null} riserler={riserler} metin={projeMetin} setMetin={setProjeMetin} durum={{ form, setForm, sayfa, setSayfa, disabled: mesgul !== null }} />}
              </>
            )}
            {t?.hedef === "donusturucu" && aile && !word && !formHatasi && tur !== "donusturucu:mrb:excel" && (
              <>
                <Baslik ara>{cevir("Send to converter")}</Baslik>
                <DevirFormu aile={aile} satir={s} riserler={riserler} exceller={exceller} durum={{ form, setForm, sayfa, setSayfa, disabled: mesgul !== null }} />
              </>
            )}
            {tur === "donusturucu:mrb:excel" && !formHatasi && (
              <div className="mt-3"><BaskaEkranKutusu ekranlar={[{ ad: "MRB intake", yol: "/mrb" }]} /></div>
            )}
            {t && !t.hedef && t.ekranlar && <div className="mt-3"><BaskaEkranKutusu ekranlar={t.ekranlar} /></div>}
          </div>
          <div className="min-w-0 space-y-3 border-t border-line px-3.5 py-3 empty:hidden @min-[52rem]:border-l @min-[52rem]:border-t-0">
            <UyariListesi s={s} kart={kart} />
            {(kart?.notlar ?? []).map((n) => <Band key={n.kalip} tip="bilgi">{kalipMetni(n.kalip, n.degerler)}</Band>)}
            {kart?.okuma && <BelgeOkumaBolumu satirId={s.id} k={kart.okuma} onYenile={() => { void kartYenile(); void onYenile(); }} />}
            {kart?.ndtOkuma && (
              <NdtOkumaBolumu
                satirId={s.id} k={kart.ndtOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur && tur ? { tur, yerKaydet, sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }), imzalayanlar: imzaBilgi.sorulur && yazabilir ? imza.sec : null } : undefined}
              />
            )}
            {kart?.weldMapOkuma && (
              <WeldMapOkumaBolumu
                satirId={s.id} k={kart.weldMapOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { yerKaydet, sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }) } : undefined}
              />
            )}
            {kart?.pwhtOkuma && (
              <PwhtOkumaBolumu
                satirId={s.id} k={kart.pwhtOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { yerKaydet, sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }) } : undefined}
              />
            )}
            {/* D-074 Faz 3 · PR 14 (M6-16): "Use the reading" yalnız formu doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.ndtSertifikaOkuma && (
              <NdtSertifikaOkumaBolumu
                satirId={s.id} k={kart.ndtSertifikaOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 3 · PR 15 (M7-17): "Use the reading" yalnız formu doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.wpqOkuma && (
              <WpqOkumaBolumu
                satirId={s.id} k={kart.wpqOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 3 · PR 16 (M1-16): cihaz seçicisi, "Use these pages" ve "Use the reading" yalnız formu doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.kalibrasyonOkuma && (
              <KalibrasyonOkumaBolumu
                satirId={s.id} k={kart.kalibrasyonOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 3 · PR 17 (M8-20): "Use the reading" yalnız formu doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.sarfOkuma && (
              <SarfOkumaBolumu
                satirId={s.id} k={kart.sarfOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 3 · PR 18 (M9-18): "Use the reading" yalnız formun Doc no.'sunu doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.wpsOkuma && (
              <WpsOkumaBolumu
                satirId={s.id} k={kart.wpsOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 4 · PR 20 (M2-20): "Use the reading" yalnız formun Doc no.'sunu ve kısmi aralığı (Pages) doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.teslimOkuma && (
              <TeslimOkumaBolumu
                satirId={s.id} k={kart.teslimOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 4 · PR 21 (M3-23): "Use these pages" ve "Use the reading" yalnız formun Doc no.'sunu ve kısmi aralığı (Pages) doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.malzemeOkuma && (
              <MalzemeOkumaBolumu
                satirId={s.id} k={kart.malzemeOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 4 · PR 22 (M4-17): "Use the reading" yalnız formun Doc no.'sunu ve kısmi aralığı (Pages) doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.uygunlukOkuma && (
              <UygunlukOkumaBolumu
                satirId={s.id} k={kart.uygunlukOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }), yerKaydet } : undefined}
              />
            )}
            {/* D-074 Faz 4 · PR 23 (M5-23): "Use the reading" yalnız formun Doc no.'sunu ve kısmi aralığı (Pages) doldurur; dosyalama bugünkü "Confirm and file"dır. */}
            {kart?.satinAlmaOkuma && (
              <SatinAlmaOkumaBolumu
                satirId={s.id} k={kart.satinAlmaOkuma} hedef={hedef} onYenile={() => { void kartYenile(); void onYenile(); }}
                form={s.eylemler.tur ? { kullan: (a) => setKutukAlan({ ...kutukAlan, ...a }), sayfalar: (x) => setSayfa({ kismi: true, sayfalar: x }), yerKaydet } : undefined}
              />
            )}
            {aile && aile !== "mrb" && s.bicim === "pdf" && pencere && (
              riserYok
                ? <Band tip="not">{cevir("A file with several risers goes through the single-riser window: {0}.", pencere.ad)}<span className="mt-0.5 block text-xs">{cevir("This file is filed on {0}. Download it and upload it there.", pencere.ad)}</span></Band>
                : <p className="text-sm text-ink-2">{cevir("A file with several risers goes through the single-riser window: {0}.", pencere.ad)}</p>
            )}
            {t?.hedef && t.hedef !== "donusturucu" && !word && !formHatasi && <EtkiKutusu baslik={cevir("Effects of filing")} metinler={etkiler(tur, etkiBaglami)} />}
            {aile && s.bicim === "pdf" && yerMetni && !word && <EtkiKutusu baslik={cevir("What the gate does")} metinler={kapiMetni(aile, yerMetni, Boolean(form.ekId))} />}
            {/* Ekran denemesi bulgusu (U-073): imzalayan dosyalayanın seçimidir ve yerle saklanmaz; dosyalayamayan kişide (ör. test teknisyeni) seçilebilir görünüyor, seçim kayboluyordu. */}
            {imzaBilgi.sorulur && t?.hedef && !word && yazabilir && <ImzalayanAlani liste={imza.liste} hata={imza.hata} secili={imza.secili} onChange={imza.sec} disabled={imza.uyari || mesgul !== null} />}
            {kapi && <KapiSonucu k={kapi} rn={rnYaz(form.rn) || ad.rn} />}
            {/* Ekran denemesi bulgusu (U-073): az önceki ret kapının cevabında ya da hata kutusunda görünüyorsa "Last attempt" onu tekrarlamaz. */}
            {s.sonDeneme && !kapi && hata !== cevir(s.sonDeneme.mesaj) && <Band tip="not">{cevir("Last attempt by {0} at {1}: {2}", s.sonDeneme.kim, tarihSaat(s.sonDeneme.ts), cevir(s.sonDeneme.mesaj))}</Band>}
            {s.dosyalayan && <Band tip="bilgi">{durumAltMetni(s)}</Band>}
            {kararKutusu && (
              <div className="rounded-lg border border-bad! bg-surface p-2.5">
                <p className="mb-2 text-sm font-semibold text-bad">{cevir("Decision needed: GEN-16 · Quality Lead or PM")}</p>
                <GerekceAlani id={`belge-${s.id}-karar`} deger={kararGerekce} onChange={setKararGerekce} zorunlu disabled={mesgul !== null} />
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.eylemler.referans && <Buton disabled={mesgul !== null} mesgul={mesgul === "referans"} onClick={() => (kararGerekce.trim().length < 3 ? setHata(cevir("Write the reason.")) : kapat("referans", kararGerekce.trim()))}>{cevir("File as reference")}</Buton>}
                  {s.eylemler.reddet && <Buton tur="tehlike" disabled={mesgul !== null} mesgul={mesgul === "reddet"} onClick={() => (kararGerekce.trim().length < 3 ? setHata(cevir("Write the reason.")) : kapat("reddet", kararGerekce.trim()))}>{cevir("Reject")}</Buton>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {hata && <div className="px-3.5 pb-3"><Hata mesaj={hata} /></div>}

      {acik && imza.uyari ? (
        <div className="border-t border-line px-3.5 pb-3">
          <ImzalayanUyarisi tur={imza.uyariTuru ?? "imzalayansiz"} bulgular={imza.uyariBulgulari} yontemler={imzaBilgi.yontemler} onOnayla={() => dosyala(true)} onGeri={imza.uyariKapat} />
        </div>
      ) : acik && (
        // Ekran denemesi bulgusu (U-073): düğmesiz kartta (Word kararı) boş gri şerit kalıyordu: içi boşsa gizlenir. History varsa köşe yuvarlanmaz.
        <div className={cn("flex flex-wrap items-center gap-2 border-t border-line bg-surface-2 px-3.5 py-2.5 empty:hidden", !(kart && kart.gecmis.length > 0) && "rounded-b-xl")}>
          {t?.hedef && t.hedef !== "donusturucu" && !word && (
            <Buton tur="birincil" disabled={Boolean(engel) || mesgul !== null} title={engel ?? undefined} mesgul={mesgul === "dosyala"} onClick={() => dosyala(false)}>{cevir("Confirm and file")}</Buton>
          )}
          {t?.hedef === "donusturucu" && aile && !word && tur !== "donusturucu:mrb:excel" && (
            <>
              {riserYok && pencere && <Link to={pencere.yol as "/"} className="inline-flex h-kontrol items-center rounded-md border border-orange bg-orange px-3 text-base font-medium text-white hover:opacity-90">{cevir("Open on {0}", pencere.ad)}</Link>}
              <Buton tur={riserYok ? "normal" : "birincil"} islem={DONUSTURUCU_YETKISI[aile]} disabled={Boolean(engel) || mesgul !== null} title={engel ?? undefined} mesgul={mesgul === "gonder" || (mesgul === "excelsiz" && !form.ekId)} onClick={() => gonder(true)}>{cevir("Send to converter")}</Buton>
              {form.ekId && <Buton islem={DONUSTURUCU_YETKISI[aile]} disabled={Boolean(engel) || mesgul !== null} title={engel ?? undefined} mesgul={mesgul === "excelsiz"} onClick={() => gonder(false)}>{cevir("Send without excel")}</Buton>}
            </>
          )}
          {gen01 && s.eylemler.zatenVar && <Buton tur={t?.hedef ? "normal" : "birincil"} disabled={mesgul !== null} mesgul={mesgul === "zaten"} onClick={() => void yaz(() => api.post<YazimYaniti>(`/belgeler/${s.id}/zaten-var`), "zaten")}>{cevir("Remove from this list")}</Buton>}
          {benim && <Buton disabled={mesgul !== null} mesgul={mesgul === "denetle"} onClick={() => void yaz(() => api.post<YazimYaniti>(`/belgeler/${s.id}/dosyalandi`), "denetle")}>{cevir("Check filing")}</Buton>}
          {!kararKutusu && kapatma === null && s.eylemler.referans && <Buton disabled={mesgul !== null} onClick={() => setKapatma({ islem: "referans", gerekce: "" })}>{cevir("File as reference")}</Buton>}
          {!kararKutusu && kapatma === null && s.eylemler.reddet && <Buton disabled={mesgul !== null} onClick={() => setKapatma({ islem: "reddet", gerekce: "" })}>{cevir("Reject")}</Buton>}
          {/* İnceleme bulgusu (U-073): pasif düğmenin nedeni yazıyla da görünür (pasif düğme klavyeyle odaklanmaz, dokunmatik ekran ipucu göstermez). */}
          {engel && t?.hedef && !word && tur !== "donusturucu:mrb:excel" && <p className="w-full text-xs text-ink-3" data-engel>{engel}</p>}
          {kapatma && (
            <div className="w-full space-y-2">
              <GerekceAlani id={`belge-${s.id}-gerekce`} deger={kapatma.gerekce} onChange={(g) => setKapatma({ ...kapatma, gerekce: g })} zorunlu={false} disabled={mesgul !== null} />
              <div className="flex flex-wrap gap-2">
                <Buton tur={kapatma.islem === "reddet" ? "tehlike" : "birincil"} mesgul={mesgul === kapatma.islem} onClick={() => kapat(kapatma.islem, kapatma.gerekce.trim())}>{kapatma.islem === "referans" ? cevir("File as reference") : cevir("Reject")}</Buton>
                <Buton disabled={mesgul !== null} onClick={() => setKapatma(null)}>{cevir("Cancel")}</Buton>
              </div>
            </div>
          )}
        </div>
      )}

      {kart && kart.gecmis.length > 0 && (
        <details className="border-t border-line px-3.5 py-2 text-sm">
          <summary className="cursor-pointer text-xs font-medium text-ink-3">{cevir("History")}</summary>
          <ul className="mt-1.5 space-y-1 border-l-2 border-line-strong pl-3 text-ink-2">
            {kart.gecmis.map((g, i) => <li key={i}><span className="mr-2 font-mono text-xs text-ink-3">{tarihSaat(g.ts)}</span>{g.kim} · {cevir(g.olay)}</li>)}
          </ul>
        </details>
      )}
    </article>
  );
}
